import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { useDispatch, useSelector } from "react-redux";
import {
  ThemeProvider,
  createTheme,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  TextField,
  Autocomplete,
  Button,
  Chip,
  LinearProgress,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Snackbar,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Link,
  Dialog,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CheckIcon from "@mui/icons-material/Check";
import { Trans as Translate } from "react-i18next";
import instance from "../../axios";
import Loader from "../../Layout/Loader";
import { fetchUserCities, setCities } from "../../Actions/Cities";
import { logInUserInfo } from "../../Actions/Users";
import { errorHandler } from "../../Helpers";
import { notifySuccess } from "../../Utils/CustomNotifcations";
import CommonButton from "../../Components/common/Button";

/* Bayanat design tokens — match HTML; fonts inherit from app */
const T = {
  bg: "#f3f4f7",
  surface: "#ffffff",
  surface2: "#f6f7fa",
  line: "#e3e6ec",
  line2: "#eef0f4",
  fg: "#1d2233",
  muted: "#667085",
  faint: "#98a1b2",
  navy: "#1b1f4b",
  navy2: "#272c66",
  green: "#10b981",
  greenDk: "#0b8a5e",
  link: "#2f80ed",
  good: "#16a34a",
  goodSoft: "#e9f9ef",
  warn: "#d97706",
  warnSoft: "#fff5e6",
  bad: "#dc2626",
  badSoft: "#fdecec",
  fontUi: "inherit",
  fontHead: "inherit",
  fontMono: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  r: "12px",
};

const TYRE_BRANDS = [
  "Bridgestone",
  "Michelin",
  "Yokohama",
  "Dunlop",
  "Hankook",
  "Continental",
  "Pirelli",
  "Goodyear",
];

const BAT_BRANDS = [
  "Varta",
  "AC Delco",
  "Bosch",
  "Amaron",
  "Exide",
  "Solite",
  "Hankook",
];

const bayanatTheme = createTheme({
  typography: {
    fontFamily: "inherit",
    fontSize: 15,
  },
  palette: {
    text: { primary: T.fg, secondary: T.muted },
    background: { default: T.bg, paper: T.surface },
    primary: { main: T.navy },
    success: { main: T.green },
    error: { main: T.bad },
    warning: { main: T.warn },
    info: { main: T.link },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: T.surface,
          minHeight: 48,
          fontSize: 16,
          "& .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1.5,
            borderColor: T.line,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: T.line,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: T.link,
            borderWidth: 1.5,
          },
          "&.Mui-focused": {
            boxShadow: "0 0 0 3px rgba(47,128,237,.14)",
          },
          "&.Mui-error": {
            backgroundColor: T.badSoft,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: T.bad,
          },
        },
        input: { padding: "11px 12px" },
        multiline: { padding: "11px 12px" },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
  },
});

const DTC_SYSTEMS = [
  ["engine", "Engine", "P0xxx"],
  ["transmission", "Transmission", "P07xx"],
  ["emission", "Emission", "P04xx"],
  ["airbag", "Airbag", "B00xx"],
  ["seating", "Seating", "B18xx"],
  ["climate", "Climate Control", "B10xx"],
  ["brakes", "Brakes", "C00xx"],
  ["steering", "Steering", "C04xx"],
  ["suspension", "Suspension", "C05xx"],
  ["modules", "Modules", "U01xx"],
  ["wiring", "Wiring", "U03xx"],
  ["datashare", "Data Sharing", "U04xx"],
];

const SECTIONS = {
  oil: ["curMil", "lastMil", "lastDate", "nextMil", "nextDate", "lastSvc"],
  tyre: ["tyreBrand", "tyreSize", "tyreExp"],
  battery: ["batBrand", "batAmp", "batExp"],
};

const EMPTY_FORM = {
  curMil: "",
  lastMil: "",
  lastDate: "",
  nextMil: "",
  nextDate: "",
  lastSvc: "",
  tyreBrand: "",
  tyreSize: "",
  tyreExp: "",
  batBrand: "",
  batAmp: "",
  batExp: "",
};

const DTC_API_FIELDS = {
  engine: "dtcEngine",
  transmission: "dtcTransmission",
  emission: "dtcEmission",
  airbag: "dtcAirbag",
  seating: "dtcSeating",
  climate: "dtcClimateControl",
  brakes: "dtcBrakes",
  steering: "dtcSteering",
  suspension: "dtcSuspension",
  modules: "dtcModules",
  wiring: "dtcWiring",
  datashare: "dtcDataSharing",
};

const VIEW_ASSESSMENT_KEY = "vehicle-assessment-view";

const toBool = (v) => v === true || v === "true" || v === 1 || v === "1";

const numOrNull = (v) => {
  const n = Number(v);
  return v === "" || v == null || Number.isNaN(n) ? null : n;
};

const strOrEmpty = (v) => (v == null ? "" : String(v));

const toSignatureDataUrl = (value) => {
  if (value == null || value === "") return "";
  let sig = String(value).trim().replace(/^"+|"+$/g, "");
  if (!sig) return "";
  if (sig.startsWith("data:image")) return sig;
  // raw base64 (optionally with whitespace/newlines)
  sig = sig.replace(/\s+/g, "");
  const mime = sig.startsWith("/9j/") ? "image/jpeg" : "image/png";
  return `data:${mime};base64,${sig}`;
};

const assessmentToForm = (data = {}) => {
  const nextForm = {
    curMil: strOrEmpty(data.currentMileage),
    lastMil: strOrEmpty(data.lastOilChangeMileage),
    lastDate: strOrEmpty(data.lastOilChangeDate).slice(0, 10),
    nextMil: strOrEmpty(data.nextOilChangeMileage),
    nextDate: strOrEmpty(data.nextOilChangeDate).slice(0, 10),
    lastSvc: strOrEmpty(data.lastServiceDetails),
    tyreBrand: strOrEmpty(data.tyreBrand),
    tyreSize: strOrEmpty(data.tyreSize),
    tyreExp: strOrEmpty(data.tyreExpDate).slice(0, 10),
    batBrand: strOrEmpty(data.batteryBrand),
    batAmp: strOrEmpty(data.batteryAmp),
    batExp: strOrEmpty(data.batteryExpDate).slice(0, 10),
  };
  const nextDtc = Object.fromEntries(
    Object.entries(DTC_API_FIELDS).map(([formKey, apiKey]) => [
      formKey,
      data[apiKey] == null ? null : toBool(data[apiKey]) ? "true" : "false",
    ]),
  );
  return { form: nextForm, dtc: nextDtc };
};

const buildAssessmentPayload = ({
  vehicle,
  form,
  dtc,
  signatureBase64,
  inspectedBy,
}) => {
  const attrs = vehicle?.attributes || {};
  const payload = {
    vin: vehicle?.vin || "",
    plate: vehicle?.vehicleLicensePlate || vehicle?.plate || "",
    contractNumber: attrs.contractNumber || attrs.c_contractNumber || "",
    currentMileage: numOrNull(form.curMil),
    lastOilChangeMileage: numOrNull(form.lastMil),
    lastOilChangeDate: form.lastDate || null,
    nextOilChangeMileage: numOrNull(form.nextMil),
    nextOilChangeDate: form.nextDate || null,
    lastServiceDetails: form.lastSvc || "",
    tyreBrand: form.tyreBrand || "",
    tyreSize: form.tyreSize || "",
    tyreExpDate: form.tyreExp || null,
    batteryBrand: form.batBrand || "",
    batteryAmp: numOrNull(form.batAmp),
    batteryExpDate: form.batExp || null,
    attributes: {
      signature: signatureBase64 || "",
      inspectedBy: inspectedBy || "",
    },
  };

  Object.entries(DTC_API_FIELDS).forEach(([formKey, apiKey]) => {
    payload[apiKey] = toBool(dtc[formKey]);
  });

  return payload;
};

const toNum = (v) => (v === "" || v == null ? NaN : Number(v));
const isoDate = (d) => {
  const x = new Date(d.getTime() - d.getTimezoneOffset() * 6e4);
  return x.toString() !== "Invalid Date" ? x.toISOString().slice(0, 10) : "";
};
const fmtDate = (s) =>
  s
    ? new Date(`${s}T00:00`).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";
const km = (v) =>
  Number.isNaN(v) ? "—" : `${Math.round(v).toLocaleString("en-US")} km`;
const fmtChipDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
const dash = (v) => (v === 0 || v ? String(v) : "—");

const parsePlateParts = (plateValue = "") => {
  const parts = String(plateValue)
    .trim()
    .toUpperCase()
    .split(/\s+/)
    .filter(Boolean);
  let plateLetters = "";
  let plateNumbers = "";
  if (parts.length >= 2) {
    plateNumbers = parts[parts.length - 1];
    plateLetters = parts.slice(0, -1).join(" ");
  } else if (parts.length === 1) {
    if (/^\d+$/.test(parts[0])) {
      plateNumbers = parts[0];
    } else {
      const match = parts[0].match(/^([A-Z]+|[0-9]{1,2})([0-9]+)$/);
      if (match) {
        plateLetters = match[1];
        plateNumbers = match[2];
      } else {
        plateLetters = parts[0];
      }
    }
  }
  return { plateLetters, plateNumbers };
};

const resolveVehicleCity = (vehicle, cities) => {
  const cityId = vehicle?.cityId;
  if (cityId == null || !Array.isArray(cities) || !cities.length) return null;
  return cities.find((c) => c.id === cityId || c.id === Number(cityId)) || null;
};

const getInspectorName = (logInUser) => {
  const fromUser =
    logInUser?.name ||
    logInUser?.attributes?.name ||
    [logInUser?.firstName, logInUser?.lastName].filter(Boolean).join(" ") ||
    logInUser?.email ||
    "";
  if (fromUser) return fromUser;
  try {
    const info = JSON.parse(localStorage.getItem("userInfoFront") || "{}");
    return (
      info?.name ||
      info?.attributes?.name ||
      [info?.firstName, info?.lastName].filter(Boolean).join(" ") ||
      info?.email ||
      "Inspector"
    );
  } catch (e) {
    return "Inspector";
  }
};

const makeInspectionRef = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const r = String(Math.floor(Math.random() * 9000) + 1000);
  return `INS-${y}-${m}-${d}${r.slice(0, 2)}${r.slice(2)}`;
};

function FieldLabel({ children }) {
  return (
    <Typography
      component="label"
      sx={{
        fontSize: 12.5,
        fontWeight: 600,
        color: "#3b4256",
        display: "block",
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

function HelperMsg({ html, error }) {
  if (!html) return null;
  return (
    <Box
      component="div"
      sx={{
        mt: 0.5,
        fontSize: 12,
        color: error ? T.bad : T.muted,
        fontWeight: error ? 500 : 400,
        "& .good": { color: T.good },
        "& .warn": { color: T.warn },
        "& .bad": { color: T.bad },
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function SecIcon({ bg, color, children }) {
  return (
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: "10px",
        bgcolor: bg,
        color,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        "& svg": { width: 21, height: 21, fill: "none", stroke: "currentColor", strokeWidth: 1.8 },
      }}
    >
      {children}
    </Box>
  );
}

function SpecCell({ label, children, full, mono, borderRight }) {
  return (
    <Box
      sx={{
        p: "11px 16px",
        borderBottom: `1px solid ${T.line2}`,
        borderRight: borderRight ? `1px solid ${T.line2}` : 0,
        gridColumn: full ? "1 / -1" : "auto",
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.09em",
          textTransform: "uppercase",
          color: T.faint,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          mt: "2px",
          fontWeight: 500,
          fontSize: mono ? 13.5 : 14,
          fontFamily: mono ? T.fontMono : undefined,
          overflowWrap: "anywhere",
          fontVariantNumeric: mono ? "tabular-nums" : undefined,
        }}
        component="div"
      >
        {children}
      </Typography>
    </Box>
  );
}

function UaePlate({ cityEn, cityAr, code, numbers }) {
  return (
    <Box
      sx={{
        alignSelf: "flex-start",
        display: "flex",
        alignItems: "stretch",
        bgcolor: "#fff",
        color: "#111",
        borderRadius: "6px",
        border: "2px solid #111",
        overflow: "hidden",
        fontFamily: T.fontHead,
        boxShadow: "0 2px 0 rgba(0,0,0,.25)",
      }}
    >
      <Box
        sx={{
          bgcolor: "#c8102e",
          color: "#fff",
          fontWeight: 700,
          fontSize: 18,
          px: "10px",
          py: "4px",
          display: "grid",
          placeItems: "center",
        }}
      >
        {code}
      </Box>
      <Box
        sx={{
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          px: "8px",
          display: "grid",
          placeItems: "center",
          borderRight: "1px solid #ccc",
          lineHeight: 1.1,
          textAlign: "center",
        }}
      >
        {cityEn}
        {cityAr ? (
          <>
            <br />
            {cityAr}
          </>
        ) : null}
      </Box>
      <Box
        sx={{
          fontWeight: 700,
          fontSize: 22,
          letterSpacing: "0.06em",
          px: "12px",
          py: "3px",
          display: "grid",
          placeItems: "center",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {numbers || "—"}
      </Box>
    </Box>
  );
}

function InspectionVehicle() {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const cities = useSelector((state) => state.Cities?.cities);
  const userCities = useSelector((state) => state.Cities?.userCities);
  const logInUser = useSelector((state) => state.logInUsers);

  const queryParams = useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search],
  );
  const isViewMode = queryParams.get("mode") === "view";
  const assessmentId = queryParams.get("assessmentId");

  const padRef = useRef(null);
  const strokesRef = useRef([]);
  const currentStrokeRef = useRef(null);
  const toastTimerRef = useRef(null);

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [dtc, setDtc] = useState(() =>
    Object.fromEntries(DTC_SYSTEMS.map(([k]) => [k, null])),
  );
  const [confirm, setConfirm] = useState(false);
  const [touched, setTouched] = useState({});
  const [signed, setSigned] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState("");
  const [nowTxt, setNowTxt] = useState("");
  const [savedTxt, setSavedTxt] = useState("Draft saves on this device");
  const [barSub, setBarSub] = useState("Draft");
  const [sectionErr, setSectionErr] = useState({});
  const [doneSummary, setDoneSummary] = useState([]);
  const [sigDataUrl, setSigDataUrl] = useState("");
  const [inspectorName, setInspectorName] = useState(() =>
    getInspectorName(null),
  );
  const [inspectionRef] = useState(() => makeInspectionRef());

  // Same login gate as submit-self-check-in
  const [loginForm, setLoginForm] = useState({
    userEmail: "",
    userPassword: "",
  });
  const [isUserLogin, setIsUserLogin] = useState(true);
  const [openLogin, setOpenLogin] = useState(true);
  const [isLoginSuccess, setIsLoginSuccess] = useState(false);
  const [isSubmittingLogin, setIsSubmittingLogin] = useState(false);

  const cityList = useMemo(() => {
    if (Array.isArray(cities) && cities.length) return cities;
    if (Array.isArray(userCities) && userCities.length) return userCities;
    return [];
  }, [cities, userCities]);

  useEffect(() => {
    const ensureCities = async () => {
      if (Array.isArray(cities) && cities.length) return;
      try {
        const response = await instance({
          url: `/api/citys/get?limit=-1`,
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });
        const rows = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        const updateCities = rows.map((item) => ({
          ...item,
          name: item?.cityEn || item?.region,
          label: item?.cityEn || item?.region,
        }));
        if (updateCities.length) dispatch(setCities(updateCities));
      } catch (e) {
        /* ignore */
      }
    };
    ensureCities();
    if (logInUser?.id && !(Array.isArray(userCities) && userCities.length)) {
      dispatch(fetchUserCities(logInUser));
    }
  }, [dispatch, cities, userCities, logInUser]);

  useEffect(() => {
    if (isViewMode) return;
    const name = getInspectorName(logInUser);
    if (name) setInspectorName(name);
  }, [logInUser, isViewMode]);
  const attrs = vehicle?.attributes || {};
  const lastOdo = Number(vehicle?.odometer) || 0;
  const draftKey = useMemo(
    () => `bayanat-ins-${vehicle?.vin || vehicle?.id || id}`,
    [vehicle, id],
  );

  const vehicleCity = useMemo(
    () => resolveVehicleCity(vehicle, cityList),
    [vehicle, cityList],
  );
  const { plateLetters, plateNumbers } = useMemo(
    () => parsePlateParts(vehicle?.vehicleLicensePlate),
    [vehicle],
  );
  const plateCode =
    plateLetters?.split(/\s+/).filter(Boolean).pop()?.slice(0, 3) ||
    plateLetters ||
    "—";
  const cityEn = vehicleCity?.cityEn || vehicleCity?.label || "UAE";
  const cityAr = vehicleCity?.cityAr || "";

  const vehicleTitle =
    [attrs.make, attrs.model].filter(Boolean).join(" ") ||
    vehicle?.label ||
    "Vehicle";
  const vehicleSub = [vehicle?.category, attrs.year, attrs.color]
    .filter(Boolean)
    .join(" · ");

  const engineText = [
    attrs.engineCapacity || attrs.engine || attrs.capacity
      ? `${attrs.engineCapacity || attrs.engine || `${attrs.capacity}L`}`
      : null,
    attrs.fuelType || attrs.fuel_type,
    attrs.engineCode,
  ]
    .filter(Boolean)
    .join(" · ");

  const fleetAccount = [
    attrs.c_accountName || attrs.accountName || attrs.fleetName,
    attrs.contractNumber ? `Contract ${attrs.contractNumber}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const regChip = fmtChipDate(vehicle?.expirationTime);
  const insChip = fmtChipDate(vehicle?.insuranceTime);
  const regExpired =
    vehicle?.expirationTime && new Date(vehicle.expirationTime) < new Date();
  const insExpired =
    vehicle?.insuranceTime && new Date(vehicle.insuranceTime) < new Date();
  const insSoon =
    vehicle?.insuranceTime &&
    !insExpired &&
    (new Date(vehicle.insuranceTime) - new Date()) / 864e5 <= 60;

  const showToast = useCallback((msg) => {
    setToast(msg);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(""), 2600);
  }, []);

  const fetchVehicle = useCallback(async () => {
    if (!id) return;
    let loginuser = null;
    try {
      loginuser = JSON.parse(localStorage.getItem("userInfoFront") || "null");
    } catch (e) {
      loginuser = null;
    }
    setLoading(true);
    setError("");
    try {
      const response = await instance({
        url: `/api/vehicles/get?search=${encodeURIComponent(id)}&all=true&userId=${loginuser?.id || ""}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      setVehicle(response?.data?.[0] || null);
      if (!response?.data?.[0]) setError("Vehicle not found");
      setIsUserLogin(true);
    } catch (err) {
      console.error("Error fetching vehicle:", err);
      if (err?.message === "No-Session/Session-Expired") {
        setIsUserLogin(false);
        setOpenLogin(true);
      }
      setVehicle(null);
      setError("Unable to load vehicle details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchVehicle();
  }, [fetchVehicle, isLoginSuccess]);

  const handleChangeLogin = (field, value) => {
    setLoginForm((prev) => ({
      ...prev,
      [field]: value?.target?.value,
    }));
  };

  const handleSubmitLoginForm = async (e) => {
    e.preventDefault();
    const { userEmail, userPassword } = loginForm;
    setIsSubmittingLogin(true);
    try {
      const userInfoFront = await instance({
        url: `/api/session`,
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        data: new URLSearchParams({
          email: userEmail,
          password: userPassword,
          app: `3b8d4deb84c0d9b65070c872e2f73626f23d8dc8789a7a05`,
        }),
      });

      // Persist session user (same keys app uses elsewhere)
      if (userInfoFront) {
        dispatch(logInUserInfo(userInfoFront)); // also sets localStorage.userInfoFront
        localStorage.setItem("userInfo", JSON.stringify(userInfoFront));
      }

      setIsLoginSuccess(true);
      setIsUserLogin(true);
      setOpenLogin(false);
      dispatch(notifySuccess("User Login successfully!"));
    } catch (error) {
      setIsLoginSuccess(false);
      errorHandler(error, dispatch);
    } finally {
      setIsSubmittingLogin(false);
    }
  };

  useEffect(() => {
    const tick = () =>
      setNowTxt(
        new Date().toLocaleString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      );
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    setBarSub(
      isViewMode ? `${inspectionRef} · View only` : `${inspectionRef} · Draft`,
    );
  }, [inspectionRef, isViewMode]);

  useEffect(() => {
    if (!vehicle) return;

    if (isViewMode) {
      const loadAssessment = async () => {
        if (!assessmentId) {
          setSavedTxt("View only · assessment id missing");
          return;
        }
        try {
          const response = await instance({
            url: `/api/vehicleassessment/${assessmentId}`,
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          });
          const data = Array.isArray(response) ? response[0] : response;
          if (data) {
            let attrs = data.attributes || {};
            if (typeof attrs === "string") {
              try {
                attrs = JSON.parse(attrs);
              } catch (e) {
                attrs = {};
              }
            }
            const mapped = assessmentToForm(data);
            setForm((prev) => ({ ...prev, ...mapped.form }));
            setDtc((prev) => ({ ...prev, ...mapped.dtc }));
            if (attrs?.inspectedBy) {
              setInspectorName(String(attrs.inspectedBy));
            }
            if (attrs?.signature) {
              setSigDataUrl(toSignatureDataUrl(attrs.signature));
            }
            setConfirm(true);
            setSigned(true);
            setSavedTxt("View only · submit disabled");
            try {
              sessionStorage.setItem(
                VIEW_ASSESSMENT_KEY,
                JSON.stringify(data),
              );
            } catch (e) {
              /* ignore */
            }
          } else {
            setSavedTxt("View only · assessment not found");
            showToast("Assessment not found");
          }
        } catch (err) {
          console.error("Error loading vehicle assessment:", err);
          setSavedTxt("View only · failed to load assessment");
          showToast("Unable to load assessment details");
        }
      };
      loadAssessment();
      return;
    }

    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) {
        setForm((prev) => ({
          ...prev,
          curMil: lastOdo ? String(lastOdo) : "",
        }));
        return;
      }
      const d = JSON.parse(raw);
      setForm((prev) => {
        const next = { ...prev };
        Object.keys(EMPTY_FORM).forEach((k) => {
          if (d[k] != null) next[k] = d[k];
        });
        return next;
      });
      if (d.dtc && typeof d.dtc === "object") {
        setDtc((prev) => ({ ...prev, ...d.dtc }));
      }
    } catch (e) {
      /* ignore */
    }
  }, [vehicle, draftKey, lastOdo, isViewMode, assessmentId, showToast]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const expiryMsg = useCallback(
    (value) => {
      if (!value) return ["", ""];
      const d = new Date(`${value}T00:00`);
      const days = Math.round((d - today) / 864e5);
      if (days < 0)
        return [
          "ok",
          `<span class="bad">Expired ${-days} day${days === -1 ? "" : "s"} ago</span>`,
        ];
      if (days <= 60)
        return ["ok", `<span class="warn">Expires in ${days} days</span>`];
      return [
        "ok",
        `<span class="good">Valid · ${Math.round(days / 30)} months left</span>`,
      ];
    },
    [today],
  );

  const rules = useMemo(
    () => ({
      curMil: () => {
        const v = toNum(form.curMil);
        if (Number.isNaN(v)) return ["err", "Enter the odometer reading"];
        if (lastOdo && v < lastOdo)
          return ["err", `Lower than last recorded (${km(lastOdo)})`];
        return [
          "ok",
          lastOdo
            ? `+${(v - lastOdo).toLocaleString()} km since last record`
            : "",
        ];
      },
      lastMil: () => {
        const v = toNum(form.lastMil);
        const c = toNum(form.curMil);
        if (Number.isNaN(v))
          return ["err", "Enter the mileage at the last oil change"];
        if (!Number.isNaN(c) && v > c)
          return ["err", "Cannot be more than current mileage"];
        return ["ok", ""];
      },
      lastDate: () => {
        if (!form.lastDate) return ["err", "Pick the last oil change date"];
        if (new Date(`${form.lastDate}T00:00`) > today)
          return ["err", "Cannot be in the future"];
        return ["ok", ""];
      },
      nextMil: () => {
        const v = toNum(form.nextMil);
        const l = toNum(form.lastMil);
        const c = toNum(form.curMil);
        if (Number.isNaN(v)) return ["err", "Enter the next due mileage"];
        if (!Number.isNaN(l) && v <= l)
          return ["err", "Must be more than last oil change mileage"];
        if (!Number.isNaN(c) && v <= c)
          return [
            "ok",
            '<span class="bad">Already overdue at current mileage</span>',
          ];
        return [
          "ok",
          Number.isNaN(c) ? "" : `Due in ${(v - c).toLocaleString()} km`,
        ];
      },
      nextDate: () => {
        if (!form.nextDate) return ["err", "Pick the next due date"];
        if (form.lastDate && form.nextDate <= form.lastDate)
          return ["err", "Must be after the last oil change date"];
        if (new Date(`${form.nextDate}T00:00`) < today)
          return [
            "ok",
            '<span class="bad">Date has passed · service overdue</span>',
          ];
        return ["ok", ""];
      },
      lastSvc: () =>
        form.lastSvc.trim().length < 3
          ? ["err", "Describe the last service"]
          : ["ok", ""],
      tyreBrand: () =>
        form.tyreBrand.trim() ? ["ok", ""] : ["err", "Enter the tyre brand"],
      tyreSize: () => {
        const v = form.tyreSize.trim();
        if (!v) return ["err", "Enter the tyre size"];
        return /^\d{3}\/\d{2}\s?Z?R\s?\d{2}/i.test(v)
          ? ["ok", ""]
          : ["ok", '<span class="warn">Check format, e.g. 265/65 R17</span>'];
      },
      tyreExp: () =>
        form.tyreExp
          ? expiryMsg(form.tyreExp)
          : ["err", "Pick the tyre expiry date"],
      batBrand: () =>
        form.batBrand.trim() ? ["ok", ""] : ["err", "Enter the battery brand"],
      batAmp: () => {
        const v = toNum(form.batAmp);
        if (Number.isNaN(v) || v <= 0)
          return ["err", "Enter the battery rating in Ah"];
        if (v < 30 || v > 250)
          return ["ok", '<span class="warn">Unusual rating, please check</span>'];
        return ["ok", ""];
      },
      batExp: () =>
        form.batExp
          ? expiryMsg(form.batExp)
          : ["err", "Pick the battery expiry date"],
    }),
    [form, lastOdo, today, expiryMsg],
  );

  const defaults = {
    curMil: lastOdo ? `Last recorded ${km(lastOdo)}` : "",
    tyreSize: "Width / profile R rim",
  };

  const fieldState = (fid) => {
    const [st, m] = rules[fid]();
    const show = touched[fid];
    return {
      invalid: show && st === "err",
      msg: ((show || st === "ok") && m) || defaults[fid] || "",
    };
  };

  const secOk = {
    oil: () => SECTIONS.oil.every((fid) => rules[fid]()[0] === "ok"),
    tyre: () => SECTIONS.tyre.every((fid) => rules[fid]()[0] === "ok"),
    battery: () => SECTIONS.battery.every((fid) => rules[fid]()[0] === "ok"),
    dtc: () => Object.values(dtc).every((v) => v !== null),
    sign: () => signed && confirm,
  };

  const doneKeys = Object.keys(secOk).filter((k) => secOk[k]());
  const progPct = (doneKeys.length / Object.keys(secOk).length) * 100;
  const left = Object.keys(secOk).length - doneKeys.length;
  const barStatus = left
    ? `${left} section${left > 1 ? "s" : ""} left`
    : "Ready to submit";

  const oilCallout = (() => {
    const c = toNum(form.curMil);
    const l = toNum(form.lastMil);
    const nx = toNum(form.nextMil);
    if (!Number.isNaN(c) && !Number.isNaN(l) && l <= c) {
      const used = c - l;
      const pct =
        !Number.isNaN(nx) && nx > l
          ? ` · ${Math.min(100, Math.round((used / (nx - l)) * 100))}% of interval used`
          : "";
      return (
        <>
          Driven since last change: <Box component="b" sx={{ color: T.fg, fontFamily: T.fontHead }}>{km(used)}</Box>
          {pct}
        </>
      );
    }
    return "Enter mileage to see distance since the last oil change.";
  })();

  const dtcFaults = Object.values(dtc).filter((v) => v === "true").length;
  const dtcUnset = Object.values(dtc).filter((v) => v === null).length;

  useEffect(() => {
    if (!vehicle || submitted || isViewMode) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({ ...form, dtc }));
      setSavedTxt(
        `Draft saved on this device · ${new Date().toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        })}`,
      );
    } catch (e) {
      /* ignore */
    }
  }, [form, dtc, draftKey, vehicle, submitted, isViewMode]);

  const redrawPad = useCallback(() => {
    const cv = padRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    const r = cv.getBoundingClientRect();
    ctx.clearRect(0, 0, r.width, r.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#14185a";
    strokesRef.current.forEach((s) => {
      for (let i = 1; i < s.length; i++) {
        const a = s[i - 1];
        const b = s[i];
        ctx.lineWidth = Math.max(
          1.4,
          Math.min(3.4, 3.6 - Math.hypot(b.x - a.x, b.y - a.y) * 0.08),
        );
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
      if (s.length === 1) {
        ctx.beginPath();
        ctx.arc(s[0].x, s[0].y, 1.5, 0, 7);
        ctx.fillStyle = "#14185a";
        ctx.fill();
      }
    });
    setSigned(strokesRef.current.length > 0);
  }, []);

  const sizePad = useCallback(() => {
    const cv = padRef.current;
    if (!cv) return;
    const r = cv.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    cv.width = Math.round(r.width * dpr);
    cv.height = Math.round(r.height * dpr);
    cv.getContext("2d").setTransform(dpr, 0, 0, dpr, 0, 0);
    redrawPad();
  }, [redrawPad]);

  useEffect(() => {
    if (submitted) return;
    sizePad();
    let rw;
    const onResize = () => {
      clearTimeout(rw);
      rw = setTimeout(sizePad, 120);
    };
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(rw);
      window.removeEventListener("resize", onResize);
    };
  }, [sizePad, submitted, loading]);

  const padPoint = (e) => {
    const r = padRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onPadDown = (e) => {
    e.preventDefault();
    padRef.current.setPointerCapture(e.pointerId);
    currentStrokeRef.current = [padPoint(e)];
    strokesRef.current.push(currentStrokeRef.current);
    redrawPad();
  };

  const onPadMove = (e) => {
    if (!currentStrokeRef.current) return;
    const events = e.getCoalescedEvents ? e.getCoalescedEvents() : [e];
    events.forEach((ev) => currentStrokeRef.current.push(padPoint(ev)));
    redrawPad();
  };

  const onPadEnd = () => {
    if (currentStrokeRef.current) currentStrokeRef.current = null;
  };

  const clearPad = () => {
    strokesRef.current = [];
    redrawPad();
  };

  const undoPad = () => {
    strokesRef.current.pop();
    redrawPad();
  };

  const setField = (fid, value) => setForm((prev) => ({ ...prev, [fid]: value }));

  const blurField = (fid) => {
    setTouched((prev) => ({ ...prev, [fid]: true }));
    if (fid === "tyreSize") {
      setForm((prev) => ({
        ...prev,
        tyreSize: prev.tyreSize.toUpperCase().replace(/\s*R\s*/, " R").trim(),
      }));
    }
  };

  const addInterval = (amount) => {
    const base = toNum(form.lastMil);
    const c = toNum(form.curMil);
    const from = Number.isNaN(base) ? c : base;
    if (Number.isNaN(from)) {
      showToast("Enter last oil change mileage first");
      return;
    }
    setField("nextMil", String(from + amount));
    setTouched((prev) => ({ ...prev, nextMil: true }));
  };

  const addMonths = (months) => {
    const l = form.lastDate;
    const d = l ? new Date(`${l}T00:00`) : new Date(today);
    d.setMonth(d.getMonth() + months);
    setField("nextDate", isoDate(d));
    setTouched((prev) => ({ ...prev, nextDate: true }));
  };

  const markAllClear = () => {
    setDtc((prev) => {
      const next = { ...prev };
      DTC_SYSTEMS.forEach(([k]) => {
        if (next[k] === null) next[k] = "false";
      });
      return next;
    });
    showToast("Unchecked systems set to No fault");
  };

  const copyVin = async () => {
    if (!vehicle?.vin) return;
    try {
      await navigator.clipboard.writeText(vehicle.vin);
      showToast("VIN copied");
    } catch (e) {
      showToast("Unable to copy VIN");
    }
  };

  const scrollToSection = (secId) => {
    document.getElementById(secId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isViewMode || submitting) return;

    const allTouched = {};
    Object.keys(rules).forEach((fid) => {
      allTouched[fid] = true;
    });
    setTouched(allTouched);
    const order = ["oil", "tyre", "battery", "dtc", "sign"];
    const errs = {};
    order.forEach((s) => {
      errs[s] = !secOk[s]();
    });
    setSectionErr(errs);
    const bad = order.find((s) => !secOk[s]());
    if (bad) {
      scrollToSection(bad);
      showToast("Some details are missing. Check the highlighted section.");
      return;
    }

    const signatureBase64 = padRef.current
      ? padRef.current.toDataURL("image/png")
      : "";
    const payload = buildAssessmentPayload({
      vehicle,
      form,
      dtc,
      signatureBase64,
      inspectedBy: inspectorName,
    });
    setSubmitting(true);
    try {
      await instance({
        url: `/api/vehicleassessment`,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        data: payload,
      });

      const faults = DTC_SYSTEMS.filter(([k]) => dtc[k] === "true").map(
        ([, l]) => l,
      );
      setDoneSummary([
        ["Current mileage", km(toNum(form.curMil))],
        [
          "Next oil change",
          `${km(toNum(form.nextMil))} · ${fmtDate(form.nextDate)}`,
        ],
        [
          "Tyre",
          `${form.tyreBrand} ${form.tyreSize} · exp ${fmtDate(form.tyreExp)}`,
        ],
        [
          "Battery",
          `${form.batBrand} ${form.batAmp} Ah · exp ${fmtDate(form.batExp)}`,
        ],
        ["DTC faults", faults.length ? faults.join(", ") : "None"],
      ]);
      if (signatureBase64) setSigDataUrl(signatureBase64);
      try {
        localStorage.removeItem(draftKey);
      } catch (err) {
        /* ignore */
      }
      setBarSub(`${inspectionRef} · Submitted`);
      setSubmitted(true);
      dispatch(notifySuccess("Inspection created successfully"));
      window.scrollTo(0, 0);
    } catch (err) {
      console.error("Error creating vehicle assessment:", err);
      errorHandler(err, dispatch);
      showToast("Unable to submit inspection. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetInspection = () => {
    setForm({ ...EMPTY_FORM, curMil: lastOdo ? String(lastOdo) : "" });
    setDtc(Object.fromEntries(DTC_SYSTEMS.map(([k]) => [k, null])));
    setConfirm(false);
    setTouched({});
    setSectionErr({});
    strokesRef.current = [];
    setSigned(false);
    setSubmitted(false);
    setBarSub(`${inspectionRef} · Draft`);
    setTimeout(sizePad, 50);
    window.scrollTo(0, 0);
  };

  const chipSx = (tone) => ({
    height: 26,
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 99,
    ...(tone === "good" && {
      bgcolor: T.goodSoft,
      color: T.good,
      border: "1px solid #c6efd6",
    }),
    ...(tone === "warn" && {
      bgcolor: T.warnSoft,
      color: T.warn,
      border: "1px solid #fbe0b4",
    }),
    ...(tone === "bad" && {
      bgcolor: T.badSoft,
      color: T.bad,
      border: "1px solid #f6c9c9",
    }),
    ...(tone === "muted" && {
      bgcolor: T.surface2,
      color: T.muted,
      border: `1px solid ${T.line}`,
    }),
  });

  const paperSec = {
    bgcolor: T.surface,
    border: `1px solid ${T.line}`,
    borderRadius: T.r,
    overflow: "hidden",
    scrollMarginTop: "70px",
  };

  const quickBtnSx = {
    border: "1px dashed #b9c3d6",
    bgcolor: T.surface2,
    borderRadius: "8px",
    px: 1.25,
    py: 0.5,
    fontSize: 12.5,
    fontWeight: 600,
    color: T.link,
    minWidth: 0,
    "&:hover": { bgcolor: T.surface2, border: "1px dashed #b9c3d6" },
  };

  const renderBar = (sub) => (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: T.navy,
        top: "env(safe-area-inset-top, 0px)",
        zIndex: 20,
      }}
    >
      <Toolbar sx={{ gap: 1.5, minHeight: "56px !important", px: 2, py: 1.25 }}>
        <IconButton
          onClick={() => history.goBack()}
          aria-label="Back"
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            bgcolor: "rgba(255,255,255,.1)",
            color: "#fff",
            "&:hover": { bgcolor: "rgba(255,255,255,.16)" },
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: T.fontHead,
              fontSize: 16,
              fontWeight: 600,
              lineHeight: 1.2,
              color: "#fff",
            }}
          >
            Vehicle Inspection
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,.7)" }}>
            {sub}
          </Typography>
        </Box>
        <Chip
          size="small"
          icon={<QrCodeScannerIcon sx={{ fontSize: "13px !important" }} />}
          label="QR scanned"
          sx={{
            bgcolor: "rgba(16,185,129,.18)",
            color: "#7ff0c4",
            fontWeight: 700,
            fontSize: 11.5,
            height: 28,
            borderRadius: 99,
            "& .MuiChip-icon": { color: "#7ff0c4" },
          }}
        />
      </Toolbar>
    </AppBar>
  );

  const renderField = (fid, label, opts = {}) => {
    const fs = fieldState(fid);

    if (opts.options) {
      return (
        <Box>
          <FieldLabel>{label}</FieldLabel>
          <Autocomplete
            id={fid}
            freeSolo
            fullWidth
            options={opts.options}
            value={form[fid] || ""}
            onChange={(_, value) => setField(fid, value || "")}
            onInputChange={(_, value, reason) => {
              if (reason === "input" || reason === "clear") {
                setField(fid, value || "");
              }
            }}
            onBlur={() => blurField(fid)}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                placeholder={opts.placeholder}
                error={fs.invalid}
                inputProps={{
                  ...params.inputProps,
                }}
              />
            )}
            ListboxProps={{
              sx: {
                fontSize: 15,
                "& .MuiAutocomplete-option": {
                  minHeight: 40,
                  py: 1,
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                minHeight: 48,
                height: 48,
                py: 0,
                pl: "12px",
                pr: "9px",
                fontSize: 16,
              },
              "& .MuiAutocomplete-input": {
                p: "11px 4px 11px 0 !important",
                height: "auto",
                boxSizing: "border-box",
                fontSize: 16,
              },
              "& .MuiAutocomplete-endAdornment": {
                right: 6,
                top: "50%",
                transform: "translateY(-50%)",
              },
              "& .MuiAutocomplete-popupIndicator": { color: T.muted },
              "& .MuiAutocomplete-clearIndicator": { color: T.muted },
            }}
          />
          <HelperMsg html={fs.msg} error={fs.invalid} />
        </Box>
      );
    }

    return (
      <Box>
        <FieldLabel>{label}</FieldLabel>
        <TextField
          id={fid}
          fullWidth
          type={opts.type || "text"}
          multiline={opts.multiline}
          rows={opts.rows}
          placeholder={opts.placeholder}
          value={form[fid]}
          onChange={(e) => setField(fid, e.target.value)}
          onBlur={() => blurField(fid)}
          error={fs.invalid}
          inputProps={opts.inputProps || {}}
          InputProps={
            opts.unit
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: T.faint }}>
                        {opts.unit}
                      </Typography>
                    </InputAdornment>
                  ),
                }
              : undefined
          }
        />
        <HelperMsg html={fs.msg} error={fs.invalid} />
      </Box>
    );
  };

  if (loading) {
    return (
      <>
        {!isUserLogin && (
          <LoginFormModal
            open={openLogin}
            onClose={() => setOpenLogin(false)}
            form={loginForm}
            handleSubmit={handleSubmitLoginForm}
            handleChange={handleChangeLogin}
            isLoading={isSubmittingLogin}
          />
        )}
        <ThemeProvider theme={bayanatTheme}>
          <Box sx={{ minHeight: "100vh", bgcolor: T.bg, fontFamily: T.fontUi }}>
            {renderBar("Loading…")}
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <Loader />
            </Box>
          </Box>
        </ThemeProvider>
      </>
    );
  }

  if (error || !vehicle) {
    return (
      <>
        {!isUserLogin && (
          <LoginFormModal
            open={openLogin}
            onClose={() => setOpenLogin(false)}
            form={loginForm}
            handleSubmit={handleSubmitLoginForm}
            handleChange={handleChangeLogin}
            isLoading={isSubmittingLogin}
          />
        )}
        <ThemeProvider theme={bayanatTheme}>
          <Box sx={{ minHeight: "100vh", bgcolor: T.bg }}>
            {renderBar("Unable to load")}
            <Typography color="text.secondary" textAlign="center" sx={{ py: 8 }}>
              {error || "Vehicle not found"}
            </Typography>
          </Box>
        </ThemeProvider>
      </>
    );
  }

  return (
    <>
      {!isUserLogin && (
        <LoginFormModal
          open={openLogin}
          onClose={() => setOpenLogin(false)}
          form={loginForm}
          handleSubmit={handleSubmitLoginForm}
          handleChange={handleChangeLogin}
          isLoading={isSubmittingLogin}
        />
      )}
    <ThemeProvider theme={bayanatTheme}>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: T.bg,
          color: T.fg,
          fontFamily: T.fontUi,
          fontSize: 15,
          lineHeight: 1.45,
          pb: isViewMode ? 4 : 15,
        }}
      >
        {renderBar(barSub)}

        {!submitted ? (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {isViewMode && (
              <Container maxWidth={false} sx={{ maxWidth: 1180, px: 2, pt: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1.5}
                  flexWrap="wrap"
                >
                  <Chip
                    label="View only"
                    size="small"
                    sx={{
                      bgcolor: "#eef2ff",
                      color: T.navy,
                      fontWeight: 700,
                      border: `1px solid ${T.line}`,
                    }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      if (id) {
                        history.push(`/vehicleServices/${id}`);
                      } else {
                        history.goBack();
                      }
                    }}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      borderColor: T.line,
                      color: T.navy,
                    }}
                  >
                    Back to table
                  </Button>
                </Stack>
              </Container>
            )}
            <Box
              component="fieldset"
              disabled={isViewMode}
              sx={{
                border: 0,
                m: 0,
                p: 0,
                minWidth: 0,
                ...(isViewMode
                  ? {
                      pointerEvents: "none",
                      opacity: 0.92,
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f8fafc",
                      },
                    }
                  : {}),
              }}
            >
            <Container
              maxWidth={false}
              sx={{
                maxWidth: 1180,
                px: 2,
                pt: { xs: 2, md: 3 },
                pb: 2,
                display: "grid",
                gap: { xs: 2, md: 2.5 },
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  md: "360px minmax(0, 1fr)",
                },
                alignItems: "start",
              }}
            >
              {/* LEFT — sticky like HTML .side (pins while right scrolls; releases at form end) */}
              <Box
                component="aside"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minWidth: 0,
                  position: { md: "sticky" },
                  top: { md: "calc(76px + env(safe-area-inset-top, 0px))" },
                  alignSelf: "start",
                  zIndex: 1,
                }}
              >
                    <Paper elevation={0} sx={{ ...paperSec }}>
                      <Box
                        sx={{
                          background: `linear-gradient(135deg, ${T.navy}, ${T.navy2})`,
                          color: "#fff",
                          p: 2,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.75,
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "12px",
                              bgcolor: "rgba(255,255,255,.1)",
                              display: "grid",
                              placeItems: "center",
                              flexShrink: 0,
                            }}
                          >
                            <DirectionsCarIcon />
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                m: 0,
                                fontFamily: T.fontHead,
                                fontSize: 19,
                                fontWeight: 600,
                                lineHeight: 1.2,
                              }}
                            >
                              {vehicleTitle}
                            </Typography>
                            <Typography sx={{ mt: "2px", fontSize: 12.5, opacity: 0.75 }}>
                              {vehicleSub || "—"}
                            </Typography>
                          </Box>
                        </Stack>
                        <UaePlate
                          cityEn={cityEn}
                          cityAr={cityAr}
                          code={plateCode}
                          numbers={plateNumbers}
                        />
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        }}
                      >
                        <SpecCell label="Make" borderRight>
                          {dash(attrs.make)}
                        </SpecCell>
                        <SpecCell label="Model">{dash(attrs.model)}</SpecCell>
                        <SpecCell label="VIN" full mono>
                          {dash(vehicle.vin)}
                          {vehicle.vin ? (
                            <Link
                              component="button"
                              type="button"
                              onClick={copyVin}
                              sx={{
                                ml: 0.75,
                                fontSize: 12,
                                fontWeight: 600,
                                color: T.link,
                                verticalAlign: "baseline",
                              }}
                            >
                              Copy
                            </Link>
                          ) : null}
                        </SpecCell>
                        <SpecCell label="Chassis No." full mono>
                          {dash(attrs.chassisNumber || vehicle.vin)}
                        </SpecCell>
                        <SpecCell label="Year" mono borderRight>
                          {dash(attrs.year)}
                        </SpecCell>
                        <SpecCell label="Engine">{dash(engineText)}</SpecCell>
                        <SpecCell label="Transmission" borderRight>
                          {dash(attrs.trasmissionType || attrs.transmission)}
                        </SpecCell>
                        <SpecCell label="Last odometer" mono>
                          {lastOdo ? km(lastOdo) : "—"}
                        </SpecCell>
                        <SpecCell label="Fleet account" full>
                          {dash(fleetAccount || vehicle.label)}
                        </SpecCell>
                      </Box>

                      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ p: "12px 16px" }}>
                        {regChip ? (
                          <Chip
                            size="small"
                            label={`${regExpired ? "Registration expired" : "Registration valid"} · ${regChip}`}
                            sx={chipSx(regExpired ? "bad" : "good")}
                          />
                        ) : null}
                        {insChip ? (
                          <Chip
                            size="small"
                            label={`${insExpired ? "Insurance expired" : "Insurance expires"} · ${insChip}`}
                            sx={chipSx(insExpired ? "bad" : insSoon ? "warn" : "good")}
                          />
                        ) : null}
                        {!regChip && !insChip ? (
                          <Chip size="small" label="No expiry dates on file" sx={chipSx("muted")} />
                        ) : null}
                      </Stack>
                    </Paper>

                    <Paper elevation={0} sx={{ ...paperSec, p: "14px 16px" }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1.25}>
                        <Typography sx={{ fontSize: 13, color: T.muted }}>Progress</Typography>
                        <Typography sx={{ fontFamily: T.fontHead, fontSize: 15, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                          {doneKeys.length} / {Object.keys(secOk).length}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={progPct}
                        sx={{
                          height: 6,
                          borderRadius: 99,
                          bgcolor: T.line2,
                          mb: 1.25,
                          "& .MuiLinearProgress-bar": { bgcolor: T.green, borderRadius: 99 },
                        }}
                      />
                      <Stack
                        direction={{ xs: "row", md: "column" }}
                        spacing={0.75}
                        sx={{ overflowX: { xs: "auto", md: "visible" }, mx: { xs: -0.5 }, px: { xs: 0.5 } }}
                      >
                        {[
                          ["oil", "Oil"],
                          ["tyre", "Tyre"],
                          ["battery", "Battery"],
                          ["dtc", "DTC Errors"],
                          ["sign", "Sign & submit"],
                        ].map(([key, label]) => {
                          const done = doneKeys.includes(key);
                          const err = sectionErr[key];
                          return (
                            <Button
                              key={key}
                              size="small"
                              onClick={() => scrollToSection(key)}
                              sx={{
                                justifyContent: "flex-start",
                                flexShrink: 0,
                                gap: 0.75,
                                px: "11px",
                                py: "7px",
                                borderRadius: { xs: 99, md: "10px" },
                                border: `1px solid ${err ? "#f3b4b4" : T.line}`,
                                bgcolor: err ? T.badSoft : T.surface,
                                color: T.fg,
                                fontSize: 13,
                                fontWeight: 500,
                                "&:hover": {
                                  bgcolor: err ? T.badSoft : T.surface2,
                                  border: `1px solid ${err ? "#f3b4b4" : T.line}`,
                                },
                              }}
                              startIcon={
                                done ? (
                                  <Box
                                    sx={{
                                      width: 18,
                                      height: 18,
                                      borderRadius: "50%",
                                      bgcolor: T.green,
                                      display: "grid",
                                      placeItems: "center",
                                    }}
                                  >
                                    <CheckIcon sx={{ fontSize: 12, color: "#fff" }} />
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      width: 18,
                                      height: 18,
                                      borderRadius: "50%",
                                      border: `2px solid ${err ? T.bad : T.line}`,
                                    }}
                                  />
                                )
                              }
                            >
                              {label}
                            </Button>
                          );
                        })}
                      </Stack>
                    </Paper>
              </Box>

              {/* RIGHT — form scrolls; left stays pinned until this column ends */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minWidth: 0,
                }}
              >
                    {/* Oil */}
                    <Paper id="oil" elevation={0} sx={paperSec}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ p: "14px 16px", borderBottom: `1px solid ${T.line2}` }}
                      >
                        <SecIcon bg="#fff5e6" color="#d97706">
                          <svg viewBox="0 0 24 24">
                            <path d="M12 3s6 6.5 6 11a6 6 0 01-12 0c0-4.5 6-11 6-11z" />
                            <path d="M9.5 14.5a2.5 2.5 0 002.5 2.5" />
                          </svg>
                        </SecIcon>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontFamily: T.fontHead, fontSize: 15.5, fontWeight: 600, lineHeight: 1.2 }}>
                            Oil
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, color: T.muted }}>
                            Mileage and service history
                          </Typography>
                        </Box>
                      </Stack>
                      <Grid container spacing={1.5} sx={{ p: 2 }}>
                        <Grid item xs={12} sm={6} md={4}>
                          {renderField("curMil", "Current Mileage", {
                            type: "number",
                            unit: "km",
                            placeholder: lastOdo ? String(lastOdo) : "0",
                            inputProps: { min: 0, step: 1, inputMode: "numeric" },
                          })}
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          {renderField("lastMil", "Last Oil Change Mileage", {
                            type: "number",
                            unit: "km",
                            inputProps: { min: 0, step: 1, inputMode: "numeric" },
                          })}
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          {renderField("lastDate", "Last Oil Change Date", { type: "date" })}
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          {renderField("nextMil", "Next Oil Change Mileage", {
                            type: "number",
                            unit: "km",
                            inputProps: { min: 0, step: 1, inputMode: "numeric" },
                          })}
                          <Stack direction="row" spacing={0.75} mt={0.75} flexWrap="wrap">
                            <Button size="small" sx={quickBtnSx} onClick={() => addInterval(5000)}>
                              +5,000
                            </Button>
                            <Button size="small" sx={quickBtnSx} onClick={() => addInterval(10000)}>
                              +10,000
                            </Button>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          {renderField("nextDate", "Next Oil Change Date", { type: "date" })}
                          <Stack direction="row" spacing={0.75} mt={0.75} flexWrap="wrap">
                            <Button size="small" sx={quickBtnSx} onClick={() => addMonths(3)}>
                              +3 months
                            </Button>
                            <Button size="small" sx={quickBtnSx} onClick={() => addMonths(6)}>
                              +6 months
                            </Button>
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: "10px 12px",
                              bgcolor: T.surface2,
                              border: `1px solid ${T.line}`,
                              borderRadius: "10px",
                              fontSize: 13,
                              color: T.muted,
                            }}
                          >
                            {oilCallout}
                          </Paper>
                        </Grid>
                        <Grid item xs={12}>
                          {renderField("lastSvc", "Last Service Details", {
                            multiline: true,
                            rows: 3,
                            placeholder:
                              "e.g. Engine oil 5W-30 (6L), oil filter, air filter replaced",
                          })}
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* Tyre */}
                    <Paper id="tyre" elevation={0} sx={paperSec}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ p: "14px 16px", borderBottom: `1px solid ${T.line2}` }}
                      >
                        <SecIcon bg="#eef2ff" color="#3b4fd8">
                          <svg viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="9" />
                            <circle cx="12" cy="12" r="3.5" />
                            <path d="M12 3v5.5M12 15.5V21M3 12h5.5M15.5 12H21" />
                          </svg>
                        </SecIcon>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontFamily: T.fontHead, fontSize: 15.5, fontWeight: 600 }}>
                            Tyre
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, color: T.muted }}>
                            Fitted tyre details
                          </Typography>
                        </Box>
                      </Stack>
                      <Grid container spacing={1.5} sx={{ p: 2 }}>
                        <Grid item xs={12} sm={4}>
                          {renderField("tyreBrand", "Brand", {
                            options: TYRE_BRANDS,
                            placeholder: "e.g. Bridgestone",
                          })}
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          {renderField("tyreSize", "Size", { placeholder: "265/65 R17" })}
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          {renderField("tyreExp", "Exp. Date", { type: "date" })}
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* Battery */}
                    <Paper id="battery" elevation={0} sx={paperSec}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ p: "14px 16px", borderBottom: `1px solid ${T.line2}` }}
                      >
                        <SecIcon bg="#e9f9ef" color="#16a34a">
                          <svg viewBox="0 0 24 24">
                            <rect x="3" y="7" width="18" height="13" rx="2" />
                            <path d="M7 7V5h3v2M14 7V5h3v2M7 13.5h3M14 13.5h3M15.5 12v3" />
                          </svg>
                        </SecIcon>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontFamily: T.fontHead, fontSize: 15.5, fontWeight: 600 }}>
                            Battery
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, color: T.muted }}>
                            Fitted battery details
                          </Typography>
                        </Box>
                      </Stack>
                      <Grid container spacing={1.5} sx={{ p: 2 }}>
                        <Grid item xs={12} sm={4}>
                          {renderField("batBrand", "Brand", {
                            options: BAT_BRANDS,
                            placeholder: "e.g. Varta",
                          })}
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          {renderField("batAmp", "Amp", {
                            type: "number",
                            unit: "Ah",
                            placeholder: "70",
                            inputProps: { min: 1, step: 1, inputMode: "numeric" },
                          })}
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          {renderField("batExp", "Exp. Date", { type: "date" })}
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* DTC */}
                    <Paper id="dtc" elevation={0} sx={paperSec}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        flexWrap="wrap"
                        sx={{ p: "14px 16px", borderBottom: `1px solid ${T.line2}` }}
                      >
                        <SecIcon bg="#fdecec" color="#dc2626">
                          <svg viewBox="0 0 24 24">
                            <path d="M4 8h3l1-2h8l1 2h3v9H4z" />
                            <path d="M8 12h2l1-2 2 4 1-2h2" />
                          </svg>
                        </SecIcon>
                        <Box sx={{ flex: 1, minWidth: 120 }}>
                          <Typography sx={{ fontFamily: T.fontHead, fontSize: 15.5, fontWeight: 600 }}>
                            DTC Errors
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, color: T.muted }}>
                            Diagnostic trouble codes by system
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          onClick={markAllClear}
                          sx={{
                            border: "1px solid #bfe9cf",
                            bgcolor: T.goodSoft,
                            color: T.greenDk,
                            borderRadius: "8px",
                            px: 1.5,
                            fontSize: 12.5,
                            fontWeight: 600,
                            "&:hover": { bgcolor: T.goodSoft },
                          }}
                        >
                          Mark all No fault
                        </Button>
                      </Stack>
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ px: 2, pt: 1.5 }}>
                        <Chip
                          size="small"
                          label={`${dtcFaults} fault${dtcFaults === 1 ? "" : "s"}`}
                          sx={chipSx(dtcFaults ? "bad" : "good")}
                        />
                        <Chip
                          size="small"
                          label={`${12 - dtcUnset} / 12 checked`}
                          sx={chipSx(dtcUnset ? "warn" : "muted")}
                        />
                      </Stack>
                      <Box
                        sx={{
                          px: 2,
                          py: 1,
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                          columnGap: { md: 3.5 },
                        }}
                      >
                        {DTC_SYSTEMS.map(([key, label, code]) => (
                          <Stack
                            key={key}
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                              py: 1.25,
                              borderBottom: `1px solid ${T.line2}`,
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  color: dtc[key] === "true" ? T.bad : T.fg,
                                }}
                              >
                                {label}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 11.5,
                                  color: T.faint,
                                  fontFamily: T.fontMono,
                                }}
                              >
                                {code}
                              </Typography>
                            </Box>
                            <ToggleButtonGroup
                              exclusive
                              size="small"
                              value={dtc[key]}
                              onChange={(_, val) => {
                                if (val != null) setDtc((prev) => ({ ...prev, [key]: val }));
                              }}
                              sx={{
                                border: `1.5px solid ${dtc[key] === null ? "#f0c48a" : T.line}`,
                                borderRadius: "10px",
                                overflow: "hidden",
                                "& .MuiToggleButton-root": {
                                  border: 0,
                                  borderRadius: 0,
                                  px: 1.25,
                                  minWidth: 76,
                                  minHeight: 40,
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: T.muted,
                                  textTransform: "none",
                                  "&.Mui-selected[value='false'], &.Mui-selected:first-of-type": {
                                    bgcolor: T.goodSoft,
                                    color: T.good,
                                  },
                                },
                              }}
                            >
                              <ToggleButton
                                value="false"
                                sx={{
                                  "&.Mui-selected": {
                                    bgcolor: `${T.goodSoft} !important`,
                                    color: `${T.good} !important`,
                                  },
                                }}
                              >
                                No fault
                              </ToggleButton>
                              <ToggleButton
                                value="true"
                                sx={{
                                  borderLeft: `1.5px solid ${T.line} !important`,
                                  "&.Mui-selected": {
                                    bgcolor: `${T.bad} !important`,
                                    color: `#fff !important`,
                                  },
                                }}
                              >
                                Fault
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </Stack>
                        ))}
                      </Box>
                      {sectionErr.dtc && dtcUnset ? (
                        <FormHelperText error sx={{ px: 2, pb: 2 }}>
                          Set No fault or Fault for {dtcUnset} system{dtcUnset > 1 ? "s" : ""}
                        </FormHelperText>
                      ) : null}
                    </Paper>

                    {/* Sign */}
                    <Paper id="sign" elevation={0} sx={paperSec}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ p: "14px 16px", borderBottom: `1px solid ${T.line2}` }}
                      >
                        <SecIcon bg="#eef2ff" color={T.navy}>
                          <svg viewBox="0 0 24 24">
                            <path d="M3 17c3 0 4-8 6-8s0 9 3 9 3-5 5-5 2 3 4 3" />
                            <path d="M3 21h18" />
                          </svg>
                        </SecIcon>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontFamily: T.fontHead, fontSize: 15.5, fontWeight: 600 }}>
                            Sign &amp; submit
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, color: T.muted }}>
                            Confirm the inspection
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack spacing={1.75} sx={{ p: 2 }}>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6}>
                            <FieldLabel>Inspected by</FieldLabel>
                            <TextField
                              size="small"
                              fullWidth
                              value={inspectorName}
                              onChange={(e) => setInspectorName(e.target.value)}
                              placeholder="Inspector name"
                              inputProps={{ readOnly: isViewMode }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  minHeight: 48,
                                  borderRadius: "10px",
                                  bgcolor: isViewMode ? T.surface2 : T.surface,
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FieldLabel>Date &amp; time</FieldLabel>
                            <Paper
                              elevation={0}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                minHeight: 48,
                                px: 1.5,
                                border: `1.5px dashed ${T.line}`,
                                borderRadius: "10px",
                                bgcolor: T.surface2,
                                fontWeight: 500,
                                color: "#3b4256",
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {nowTxt}
                            </Paper>
                          </Grid>
                        </Grid>

                        <Box>
                          <FieldLabel>Signature</FieldLabel>
                          <Paper
                            elevation={0}
                            sx={{
                              position: "relative",
                              border: `1.5px solid ${
                                sectionErr.sign && !signed ? T.bad : T.line
                              }`,
                              borderRadius: "12px",
                              overflow: "hidden",
                            }}
                          >
                            {isViewMode ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minHeight: { xs: 200, md: 220 },
                                  bgcolor: "#fff",
                                  p: 2,
                                }}
                              >
                                {sigDataUrl ? (
                                  <Box
                                    component="img"
                                    src={sigDataUrl}
                                    alt="Inspector signature"
                                    sx={{
                                      maxWidth: "100%",
                                      maxHeight: 200,
                                      objectFit: "contain",
                                    }}
                                  />
                                ) : (
                                  <Typography
                                    sx={{ color: T.faint, fontSize: 13.5 }}
                                  >
                                    No signature available
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <>
                                <Box
                                  component="canvas"
                                  ref={padRef}
                                  onPointerDown={onPadDown}
                                  onPointerMove={onPadMove}
                                  onPointerUp={onPadEnd}
                                  onPointerCancel={onPadEnd}
                                  sx={{
                                    display: "block",
                                    width: "100%",
                                    height: { xs: 200, md: 220 },
                                    touchAction: "none",
                                    cursor: "crosshair",
                                  }}
                                />
                                {!signed ? (
                                  <Typography
                                    sx={{
                                      position: "absolute",
                                      inset: 0,
                                      display: "grid",
                                      placeItems: "center",
                                      color: T.faint,
                                      fontSize: 13.5,
                                      pointerEvents: "none",
                                    }}
                                  >
                                    Sign here with your finger
                                  </Typography>
                                ) : null}
                              </>
                            )}
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{
                                px: 1.5,
                                py: 1,
                                bgcolor: T.surface2,
                                borderTop: `1px solid ${T.line2}`,
                                fontSize: 12,
                                color: T.muted,
                              }}
                            >
                              <span>
                                {isViewMode
                                  ? sigDataUrl
                                    ? "Signed"
                                    : "No signature"
                                  : signed
                                    ? "Signed"
                                    : "Not signed"}
                              </span>
                              {!isViewMode && (
                                <Stack direction="row" spacing={0.75}>
                                  <Button
                                    size="small"
                                    onClick={undoPad}
                                    sx={{
                                      border: `1px solid ${T.line}`,
                                      borderRadius: "8px",
                                      fontSize: 12.5,
                                      fontWeight: 600,
                                      minWidth: 0,
                                    }}
                                  >
                                    Undo
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={clearPad}
                                    sx={{
                                      border: `1px solid ${T.line}`,
                                      borderRadius: "8px",
                                      fontSize: 12.5,
                                      fontWeight: 600,
                                      minWidth: 0,
                                    }}
                                  >
                                    Clear
                                  </Button>
                                </Stack>
                              )}
                            </Stack>
                          </Paper>
                          {sectionErr.sign && !signed ? (
                            <FormHelperText error>Add your signature</FormHelperText>
                          ) : null}
                        </Box>

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={confirm}
                              onChange={(e) => setConfirm(e.target.checked)}
                              sx={{
                                color: T.green,
                                "&.Mui-checked": { color: T.green },
                                "& .MuiSvgIcon-root": { fontSize: 20 },
                              }}
                            />
                          }
                          label="I confirm the details above were checked on this vehicle and are correct."
                          sx={{
                            alignItems: "flex-start",
                            m: 0,
                            "& .MuiFormControlLabel-label": {
                              fontSize: 13.5,
                              color: "#3b4256",
                              pt: 0.5,
                            },
                          }}
                        />
                        {sectionErr.sign && !confirm ? (
                          <FormHelperText error>Tick to confirm the inspection</FormHelperText>
                        ) : null}
                      </Stack>
                    </Paper>
              </Box>
            </Container>
            </Box>

            {/* Sticky submit bar — hidden in view mode */}
            {!isViewMode && (
              <Paper
                elevation={0}
                sx={{
                  position: "fixed",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 20,
                  bgcolor: T.surface,
                  borderTop: `1px solid ${T.line}`,
                  borderRadius: 0,
                  boxShadow: "0 -6px 20px rgba(16,24,40,.06)",
                  px: 2,
                  pt: 1.5,
                  pb: "calc(12px + env(safe-area-inset-bottom, 0px))",
                }}
              >
                <Box
                  sx={{
                    maxWidth: 1180,
                    mx: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        display: "block",
                        color: T.fg,
                        fontSize: 14,
                        fontFamily: T.fontHead,
                        fontWeight: 600,
                      }}
                    >
                      {barStatus}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: T.faint }}>
                      {savedTxt}
                    </Typography>
                  </Box>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                    startIcon={<CheckIcon />}
                    sx={{
                      bgcolor: T.green,
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 15,
                      letterSpacing: "0.02em",
                      borderRadius: "10px",
                      minHeight: 48,
                      px: { xs: 2, sm: 2.75 },
                      whiteSpace: "nowrap",
                      boxShadow: "none",
                      "&:hover": { bgcolor: "#0ea371", boxShadow: "none" },
                      "&.Mui-disabled": {
                        bgcolor: "rgba(16, 185, 129, 0.5)",
                        color: "#fff",
                      },
                    }}
                  >
                    {submitting ? "Submitting…" : "Submit inspection"}
                  </Button>
                </Box>
              </Paper>
            )}
          </Box>
        ) : (
          <Container maxWidth="sm" sx={{ py: 3.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...paperSec,
                p: "24px 20px",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.75,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: T.goodSoft,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <CheckIcon sx={{ fontSize: 32, color: T.good }} />
              </Box>
              <Typography sx={{ fontFamily: T.fontHead, fontSize: 20, fontWeight: 600 }}>
                Inspection submitted
              </Typography>
              <Typography sx={{ color: T.muted }}>
                {vehicleTitle} · {cityEn} {plateCode} {plateNumbers}
              </Typography>
              <Box
                sx={{
                  fontFamily: T.fontMono,
                  fontSize: 15,
                  bgcolor: T.surface2,
                  border: `1px solid ${T.line}`,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: "8px",
                }}
              >
                {inspectionRef}
              </Box>
              <Box
                sx={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gap: "8px 14px",
                  fontSize: 13.5,
                  borderTop: `1px solid ${T.line2}`,
                  pt: 1.75,
                  textAlign: "left",
                }}
              >
                {doneSummary.map(([k, v]) => (
                  <React.Fragment key={k}>
                    <Typography sx={{ color: T.muted }}>{k}</Typography>
                    <Typography sx={{ fontWeight: 500, textAlign: "right" }}>{v}</Typography>
                  </React.Fragment>
                ))}
              </Box>
              {sigDataUrl ? (
                <Box
                  component="img"
                  src={sigDataUrl}
                  alt="Inspector signature"
                  sx={{ maxWidth: 240, borderBottom: `1.5px solid ${T.line}` }}
                />
              ) : null}
              <Typography sx={{ fontSize: 13, color: T.muted }}>
                Signed by {inspectorName || "Inspector"} · {nowTxt}
              </Typography>
            </Paper>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mt: 2 }}>
              <Button
                fullWidth
                onClick={resetInspection}
                sx={{
                  minHeight: 46,
                  border: `1.5px solid ${T.line}`,
                  bgcolor: T.surface,
                  borderRadius: "10px",
                  fontWeight: 600,
                  color: T.fg,
                  "&:hover": { bgcolor: T.surface2 },
                }}
              >
                Inspect again
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  if (id) {
                    history.push(`/vehicleServices/${id}`);
                  } else {
                    history.goBack();
                  }
                }}
                sx={{
                  minHeight: 46,
                  borderRadius: "10px",
                  fontWeight: 600,
                  bgcolor: T.navy,
                  color: "#fff",
                  boxShadow: "none",
                  "&:hover": { bgcolor: T.navy2, boxShadow: "none" },
                }}
              >
                Back to table
              </Button>
            </Stack>
          </Container>
        )}

        <Snackbar
          open={Boolean(toast)}
          message={toast}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          ContentProps={{
            sx: {
              bgcolor: T.navy,
              color: "#fff",
              fontSize: 13.5,
              fontWeight: 500,
              borderRadius: "10px",
              boxShadow: "0 8px 24px rgba(0,0,0,.2)",
            },
          }}
          sx={{ bottom: { xs: 96, sm: 96 } }}
        />
      </Box>
    </ThemeProvider>
    </>
  );
}

const LoginFormModal = ({
  open,
  onClose,
  form,
  handleChange,
  handleSubmit,
  isLoading,
}) => {
  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    onClose();
  };
  return (
    <Dialog open={open} onClose={handleDialogClose}>
      <DialogContent style={{ padding: "0rem" }}>
        <div style={{ padding: "0rem" }}>
          <Paper elevation={3} style={{ padding: "2rem" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 1,
                padding: "0.5rem 0rem",
                backgroundColor: "#fff",
                borderRadius: 2,
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              <img src="/logo.png" alt="Logo" style={{ height: 50 }} />
            </Box>
            <form autoComplete="off" onSubmit={handleSubmit}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <TextField
                    id="email"
                    label={<Translate i18nKey="email" />}
                    required
                    value={form?.userEmail || ""}
                    onChange={(e) => handleChange("userEmail", e)}
                    variant="outlined"
                    type="email"
                    margin="dense"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="password"
                    label={<Translate i18nKey="password" />}
                    type="password"
                    value={form?.userPassword || ""}
                    onChange={(e) => handleChange("userPassword", e)}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} style={{ textAlign: "center" }}>
                  <CommonButton
                    type="submit"
                    style={{ width: "25%" }}
                    className="btn-submit theme-green"
                    disabled={isLoading}
                    startIcon={
                      isLoading && (
                        <CircularProgress size={20} color="inherit" />
                      )
                    }
                  >
                    {isLoading ? (
                      <Translate i18nKey="loading">Loading...</Translate>
                    ) : (
                      <Translate i18nKey="loginLogin">Submit</Translate>
                    )}
                  </CommonButton>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InspectionVehicle;
